import axios from "axios";

// Track if token refresh is in progress
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken) {
    console.warn('No refresh token, redirecting to login...');
    clearAllAuthData();
    return null;
  }

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_PATH}auth/refresh-token`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    if (response.status === 200 && response.data.data) {
      const { token, refreshToken: newRefreshToken } = response.data.data;

      localStorage.setItem('jwtToken', token);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      localStorage.setItem('tokenExpiry', Date.now() + (4 * 60 * 60 * 1000));

      console.log('Token refreshed successfully');
      return token;
    }

    clearAllAuthData();
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    clearAllAuthData();
    return null;
  }
}

function clearAllAuthData() {
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('tokenExpiry');
  localStorage.removeItem('adminAccess');
  localStorage.removeItem('user_name');
  localStorage.removeItem('userId');
  localStorage.removeItem('userEmail');
  window.location.href = '/login';
}

// Axios interceptor for automatic token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || '';

    // Validation / SQL / business errors must stay on the page — never trigger relogin
    if (!originalRequest || status !== 401 || requestUrl.includes('/auth/refresh-token')) {
      return Promise.reject(error);
    }

    // Token expired — try refresh once, then relogin only if still unauthorized
    if (originalRequest._retry) {
        console.error('Token refresh failed, redirecting to login...');
        clearAllAuthData();
        return Promise.reject(error);
    }

    originalRequest._retry = true;

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axios(originalRequest);
        })
        .catch(err => Promise.reject(err));
    }

    isRefreshing = true;

    try {
      // Try to refresh the token
      const newToken = await refreshAccessToken();

      if (!newToken) {
        // Refresh failed, clearAllAuthData already called
        return Promise.reject(error);
      }

      // Process queued requests
      processQueue(null, newToken);

      // Retry the original request with new token
      originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export const functionService = {
  get,
  post,
  update,
  remove,
  checkValidation,
  uploadFile,
  patch,
  put,
  getDemandDetails,
  getInterviewDebriefReport,
  saveDebriefNoReason,
  getJobListings,
  getProfileListings,
  getProfileSentListings,
  getInterviewListings,
  rescheduleInterview,
  getSourcingReport,
  updateCandidateStatus,
  getCandidateInfo,
  insertCandidateInfo,
  sentStatusToCompany,
  getCompanyFirstPage,
  getCompanyListings,
  setCandidateRemarks,
  getStatusRemarks,
  isEmailExist,
  getMockDetails,
  sendEmailCandidate,
  sendEmailInterviewer,
  insertCompanyEmail,
  sendMailToCompany,
  sendCompanyCode,
  sendOtpMailToCompany,
  verifyOtp,
  deleteFromDemandDetails,
  getDetailsFromDemandAndRecruiter,
  updateDemandAndRecruiterDetails,
  downloadAssessmentPdf,
  getReportWithResume,
  getSkillScore,
  getDemandDashboardListings,
  updateDemandDashboard,
  bulkUploadDemandDashboard,
  postFormData,
  updateIntelligenceReportCheck,
  checkAdminUserLogin,
  getRecruitersActiveList,
  toggleRecruiterActiveStatus,
  updateRecruiterAdminAccess,
  adminSignup,
  parseResume,
  getDebriefHistory,
  updateTodayDemand,
  editInterviewDateTime,
  getRolesList,
  getSkillsList,
  getCoffeeePlusCandidates,
  getCoffeeePlusCandidateDetails,
  parseCoffeeePlusJD,
  parseCoffeeePlusKeywords,
  getCoffeeePlusAiScore,
  getReviewDashboard,
};
function post(path, formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["Authorization"] = "Basic " + currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["data"] = formdata;
  }

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}${path}`,
      formdata,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      // Don't show error message for 401 as interceptor will handle redirect
      if (error.response && error.response.status === 401) {
        return { status: false, error, isAuthError: true };
      }
      return { status: false, error };
    });
}
function update(path, formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["Authorization"] = "Basic " + currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["data"] = formdata;
  }
  return axios
    .put(
      `${process.env.REACT_APP_API_PATH}${path}`,
      formdata,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}
function patch(path, formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["Authorization"] = "Basic " + currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["data"] = formdata;
  }
  return axios
    .patch(
      `${process.env.REACT_APP_API_PATH}${path}`,
      formdata,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}
function put(path, formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["Authorization"] = "Basic " + currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["data"] = formdata;
  }
  return axios
    .put(
      `${process.env.REACT_APP_API_PATH}${path}`,
      formdata,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}
function remove(path, formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["Authorization"] = "Basic " + currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["data"] = formdata;
  }
  return axios
    .delete(
      `${process.env.REACT_APP_API_PATH}${path}`,
      axiosRequestOptions,
      formdata
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}
function get(path, formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["body"] = JSON.stringify(formdata);
  }

  return axios
    .get(`${process.env.REACT_APP_API_PATH}${path}`, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      // Don't show error message for 401 as interceptor will handle redirect
      if (error.response && error.response.status === 401) {
        return { status: false, error, isAuthError: true };
      }
      return { status: false, error };
    });
}
function checkValidation(formData) {
  let errors = formData.errors;
  let num = 0;
  Object.keys(errors).forEach(function (key) {
    if (
      typeof formData.formData[key] === "string" &&
      formData.formData[key].trim() === ""
    ) {
      errors[key] = true;
      num++;
    } else {
      errors[key] = false;
    }
  });
  formData.errors = errors;
  if (num === 0) {
    return false;
  } else {
    return formData;
  }
}
function uploadFile(file) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
  };
  const formData = new FormData();
  formData.append("inputFile", file);

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
    formData.append("token", currentUser);
  }
  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}uploads`,
      formData,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}



// function getDemandDetails(formdata = {}) {
//   let axiosRequestOptions = {
//     headers: {
//       'Content-Type': 'application/json'
//     },
//   };

//   let currentUser = localStorage.getItem("jwtToken");

//   if (currentUser) {
//     axiosRequestOptions["headers"]["authorization"] = currentUser;
//     formdata["token"] = currentUser;
//     axiosRequestOptions["headers"]["body"] = JSON.stringify(formdata);
//   }

//   axiosRequestOptions.params = formdata;

//   return axios
//     .get(
//       `${process.env.REACT_APP_API_PATH}demand/demand-details-list`,
//       axiosRequestOptions
//     )
//     .then((response) => {
//       return { status: true, response };
//     })
//     .catch((error) => {
//       return { status: false, error };
//     });
// }

function getDemandDetails(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
  };

  let currentUser = localStorage.getItem("jwtToken");

  if (currentUser) {
    axiosRequestOptions.headers.authorization = currentUser;
    formdata.token = currentUser;
  }

  axiosRequestOptions.params = formdata;

  return axios
    .get(
      `${process.env.REACT_APP_API_PATH}demand/demand-details-list`,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("[getDemandDetails] Request failed:", error.message);
      return { status: false, error };
    });
}

function getJobListings(id) {
  const formdata = {};
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["body"] = JSON.stringify(formdata);
  }

  return axios
    .get(`${process.env.REACT_APP_API_PATH}demand/get-all-call-list/${id}`, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });

}
function getSkillScore(assessmentId) {
  let axiosRequestOptions = {
    headers: { "Content-Type": "application/json" },
  };
  const currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers.authorization = currentUser;
  }
  return axios
    .get(`${process.env.REACT_APP_API_PATH}demand/skill-score/${assessmentId}`, axiosRequestOptions)
    .then((response) => ({ status: true, response }))
    .catch((error) => ({ status: false, error }));
}
function getProfileListings(params = {}) {
  const formdata = {};
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
    params, // ← sends page, limit, filters as query params automatically
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["body"] = JSON.stringify(formdata);
  }

  return axios
    .get(`${process.env.REACT_APP_API_PATH}demand/get-all-profile-list`, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}

function updateCandidateStatus(id, formdata = {}) {


  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");

  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/update-candidate-status/${id}`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in updateCandidateStatus:", error.response || error.message);
      return { status: false, error };
    });
}

function getCandidateInfo(id) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/get-candidate-info/${id}`, {}, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in getCandidateInfo:", error.response || error.message);
      return { status: false, error };
    });

}

function insertCandidateInfo(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/insert-candidate-info`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in insertCandidateInfo:", error.response || error.message);
      return { status: false, error };
    });

}



function sentStatusToCompany(id, formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/sent-to-company-status/${id}`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in sentStatusToCompany:", error.response || error.message);
      return { status: false, error };
    });

}

function getCompanyFirstPage(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/company-first-page`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in getCompanyFirstPage:", error.response || error.message);
      return { status: false, error };
    });

}
function getCompanyListings(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/company-listing`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in getCompanyListings:", error.response || error.message);
      return { status: false, error };
    });

}
function setCandidateRemarks(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/set-candidate-remarks`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in setCandidateRemarks:", error.response || error.message);
      return { status: false, error };
    });



}
function getStatusRemarks(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/get-status-remarks`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in getStatusRemarks:", error.response || error.message);
      return { status: false, error };
    });



}
function isEmailExist(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/company-mail-exist`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error:", error.response || error.message);
      return { status: false, error };
    });
}

function getMockDetails(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/get-candidate-report-company/?owner=ADMIN`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error:", error.response || error.message);
      return { status: false, error };
    });
}
function sendEmailCandidate(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/send-assessment-email-candidate`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error:", error.response || error.message);
      return { status: false, error };
    });
}
function sendEmailInterviewer(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/send-assessment-email-interviewer`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error:", error.response || error.message);
      return { status: false, error };
    });
}
function sendMailToCompany(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/send-profile-mail-to-company`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error:", error.response || error.message);
      return { status: false, error };
    });
}


function insertCompanyEmail(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/insert-company-email`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error:", error.response || error.message);
      return { status: false, error };
    });
}
function sendCompanyCode(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/insert-company-code`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error:", error.response || error.message);
      return { status: false, error };
    });
}

function sendOtpMailToCompany(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/send-otp-mail-to-company`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error:", error.response || error.message);
      return { status: false, error };
    });
}
function verifyOtp(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/verify-otp`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error:", error.response || error.message);
      return { status: false, error };
    });
}

function deleteFromDemandDetails(formdata) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/delete-from-demand-details`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error:", error.response || error.message);
      return { status: false, error };
    });
}

function getDetailsFromDemandAndRecruiter(formdata) {
  console.log("Request data:", formdata);
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}demand/get-from-demand-recruiter`,
      formdata,
      axiosRequestOptions
    )
    .then((response) => {
      console.log("API Success Response:", response);
      return { status: true, response };
    })
    .catch((error) => {
      console.error("API Error:", error.response || error.message);
      return { status: false, error };
    });
}

function updateDemandAndRecruiterDetails(formdata) {
  console.log("Request data:", formdata);
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}demand/update-recruiter-and-demand-details`,
      formdata,
      axiosRequestOptions
    )
    .then((response) => {
      console.log("API Success Response:", response);
      return { status: true, response };
    })
    .catch((error) => {
      console.error("API Error:", error.response || error.message);
      return { status: false, error };
    });
}

function getProfileSentListings(params = {}) {
  const formdata = {};
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
    params, // ← sends page, limit, and all filters as query params, same as getProfileListings
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["body"] = JSON.stringify(formdata);
  }

  return axios
    .get(`${process.env.REACT_APP_API_PATH}demand/profile-sent-listings`, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}

function getInterviewDebriefReport(startDate, endDate) {
  const formdata = {};

  let axiosRequestOptions = {
    headers: {
      "Content-Type": "application/json",
    },
    params: {
      start_date: startDate,
      end_date: endDate,
    },
  };

  let currentUser = localStorage.getItem("jwtToken");

  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["body"] = JSON.stringify(formdata);
  }

  return axios
    .get(
      `${process.env.REACT_APP_API_PATH}demand/interview-debrief-report`,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}
function saveDebriefNoReason(payload) {
  let axiosRequestOptions = {
    headers: { "Content-Type": "application/json" },
  };

  let currentUser = localStorage.getItem("jwtToken");

  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
  }

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}demand/save-debrief-no-reason`,
      payload,
      axiosRequestOptions
    )
    .then((response) => ({ status: true, response }))
    .catch((error) => ({ status: false, error }));
}
function getInterviewListings() {
  const formdata = {};
  let axiosRequestOptions = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  let currentUser = localStorage.getItem("jwtToken");

  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["body"] = JSON.stringify(formdata);
  }

  return axios
    .get(
      `${process.env.REACT_APP_API_PATH}demand/get-all-interview-list`,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}

function rescheduleInterview(data) {

  let axiosRequestOptions = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  let currentUser = localStorage.getItem("jwtToken");

  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
  }

  return axios
    .put(
      `${process.env.REACT_APP_API_PATH}demand/reschedule-interview`,
      data,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}

/**
 * Download assessment report as PDF
 * @param {HTMLElement} element - The DOM element to convert to PDF
 * @param {string} filename - The name of the PDF file
 * @returns {Promise<void>}
 */
async function downloadAssessmentPdf(element, filename) {
  if (!element) {
    console.error("No element provided for PDF generation");
    return;
  }

  try {
    const html2pdf = (await import("/node_modules/html2pdf.js/dist/html2pdf.min")).default;

    // Calculate page height
    let body = document.body, html = document.documentElement;
    let height = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
    let heightCM = height / 5;

    // PDF generation options
    var opt = {
      margin: [1, 0.5, 1.3, 0.5],
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        letterRendering: true,
        useCORS: true,
        logging: false
      },
      pagebreak: ["avoid-all", "css", "legacy"],
      jsPDF: {
        orientation: "portrait",
        unit: "cm",
        format: [heightCM, 23],
        putOnlyUsedFonts: true,
        floatPrecision: 16
      },
    };

    // Generate the PDF blob
    const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');

    // Create a secure download link
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { status: true, message: "PDF downloaded successfully" };
  } catch (error) {
    console.error("Error downloading PDF:", error);
    return { status: false, error };
  }
}

function getReportWithResume(path) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    responseType: 'arraybuffer',
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .get(`${process.env.REACT_APP_API_PATH}${path}`, axiosRequestOptions)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error in getReportWithResume:", error.response || error.message);
      throw error;
    });
}

function getDemandDashboardListings() {
  const formdata = {};
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json'
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
    formdata["token"] = currentUser;
    axiosRequestOptions["headers"]["body"] = JSON.stringify(formdata);
  }

  return axios
    .get(`${process.env.REACT_APP_API_PATH}demand/get-all-demand-dashboard-list`, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}

function updateDemandDashboard(id, formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/update-demand-dashboard/${id}`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in updateDemandDashboard:", error.response || error.message);
      return { status: false, error };
    });
}

function bulkUploadDemandDashboard(data) {
  let axiosRequestOptions = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  let currentUser = localStorage.getItem("jwtToken");

  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}demand/bulk-upload-demand-dashboard`,
      data,
      axiosRequestOptions
    )
    .then((response) => {
      return {
        status: true,
        response,
      };
    })
    .catch((error) => {
      console.error(
        "Error in bulkUploadDemandDashboard:",
        error.response || error.message
      );

      return {
        status: false,
        error,
      };
    });
}

function postFormData(path, formData) {
  let axiosRequestOptions = {
    headers: {
      // Do NOT set Content-Type manually — browser sets the correct
      // multipart boundary automatically when you leave this unset
      // for a FormData body.
    },
  };
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions["headers"]["Authorization"] = "Basic " + currentUser;
  }

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}${path}`,
      formData,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      if (error.response && error.response.status === 401) {
        return { status: false, error, isAuthError: true };
      }
      return { status: false, error };
    });
}


/**
 * Fetch (or generate, if not already available) the AI sourcing report
 * for a demand.
 *
 * The backend uses the demand_id to fetch the company name, role title,
 * and JD from demand_details. If a report already exists for the same
 * company + role, the backend returns the existing report.
 *
 * @param {{ demand_id: string }} formdata
 */
function getSourcingReport(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");

  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}demand/sourcing-report`,
      {
        demand_id: formdata.demand_id,
      },
      axiosRequestOptions
    )
    .then((response) => {
      return {
        status: true,
        response,
      };
    })
    .catch((error) => {
      console.error(
        "Error in getSourcingReport:",
        error.response || error.message
      );

      return {
        status: false,
        error,
      };
    });
}


/**
 * Parse an uploaded resume PDF and return structured candidate data
 * via the backend's PDF-extraction + OpenAI pipeline.
 *
 * Only call this when the candidate email check has already returned
 * "not found" — see demand/parse-resume backend flow.
 *
 * @param {{ cv_url: string }} formdata
 */
function parseResume(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}demand/parse-resume`,
      {
        cv_url: formdata.cv_url,
      },
      axiosRequestOptions
    )
    .then((response) => {
      return {
        status: true,
        response,
      };
    })
    .catch((error) => {
      console.error(
        "Error in parseResume:",
        error.response || error.message
      );

      return {
        status: false,
        error,
      };
    });
}


function updateIntelligenceReportCheck(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");

  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  console.log(
    "updateIntelligenceReportCheck formdata:",
    formdata
  );

  return axios
    .put(
      `${process.env.REACT_APP_API_PATH}demand/update-intelligence-report-check`,
      {
        demand_detail_recruiter_id:
          formdata.demand_detail_recruiter_id,
      },
      axiosRequestOptions
    )
    .then((response) => {
      return {
        status: true,
        response,
      };
    })
    .catch((error) => {
      console.error(
        "Error in updateIntelligenceReportCheck:",
        error.response || error.message
      );

      return {
        status: false,
        error,
      };
    });
}


function checkAdminUserLogin(email) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
 
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/admin-users/check-login`, { email }, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in checkAdminUserLogin:", error.response || error.message);
      return { status: false, error };
    });
}
 
/**
 * Returns every recruiter (admin_access = 3) with their active status,
 * so an admin can see who is active/inactive and toggle each one.
 */
function getRecruitersActiveList() {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
 
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
 
  return axios
    .get(`${process.env.REACT_APP_API_PATH}demand/admin-users/recruiters-list`, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in getRecruitersActiveList:", error.response || error.message);
      return { status: false, error };
    });
}
 
/**
 * Toggles a recruiter's active status (active <-> inactive).
 * @param {string} id - admin_users.id
 */
function toggleRecruiterActiveStatus(id) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
 
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
 
  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/admin-users/toggle-active/${id}`, {}, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in toggleRecruiterActiveStatus:", error.response || error.message);
      return { status: false, error };
    });
}
 

function updateRecruiterAdminAccess(id, admin_access) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
 
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
 
  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}demand/admin-users/update-access/${id}`,
      { admin_access },
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in updateRecruiterAdminAccess:", error.response || error.message);
      return { status: false, error };
    });
}

function adminSignup(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
 
  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }
 
  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}auth/signup`,
      {
        email: formdata.email,
        password: formdata.password,
        name: formdata.name,
        access: formdata.access,
        inter: formdata.inter,
      },
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in adminSignup:", error.response || error.message);
      return { status: false, error };
    });
}

function getDebriefHistory(data) {
  return post("demand/get-debrief-history", data);
}

function updateTodayDemand(formdata) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}demand/update-today-demand`,
      formdata,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("API Error:", error.response || error.message);
      return { status: false, error };
    });
}

function editInterviewDateTime(data) {
  let axiosRequestOptions = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  let currentUser = localStorage.getItem("jwtToken");

  if (currentUser) {
    axiosRequestOptions["headers"]["authorization"] = currentUser;
  }

  return axios
    .put(
      `${process.env.REACT_APP_API_PATH}demand/edit-interview-datetime`,
      data,
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      return { status: false, error };
    });
}


function getRolesList() {
  return get("demand/roles-list");
}
function getSkillsList() {
  return get("demand/skills-list");
}

function getCoffeeePlusCandidates(formdata = {}) {
    return post("demand/coffeee-plus-candidates", formdata);
}

function getCoffeeePlusCandidateDetails(candidateId) {
    return get(`demand/coffeee-plus-candidate/${candidateId}`);
}

function parseCoffeeePlusJD(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/coffeee-plus-parse-jd`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in parseCoffeeePlusJD:", error.response || error.message);
      return { status: false, error };
    });
}

/**
 * Coffeee Plus "Generate AI score": scores one candidate against the current
 * search. Body: { candidate_id } plus exactly one of jd_text, jd_url,
 * jd_image_url or keyword_criteria. The backend returns the score without
 * saving it. Same auth header and { status, response | error } return shape
 * as parseCoffeeePlusJD.
 */
function getCoffeeePlusAiScore(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(`${process.env.REACT_APP_API_PATH}demand/coffeee-plus-ai-score`, formdata, axiosRequestOptions)
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      console.error("Error in getCoffeeePlusAiScore:", error.response?.status || error.message);
      return { status: false, error };
    });
}

/**
 * Coffeee Plus keyword search: sends the recruiter's short query
 * (e.g. "React Node Bangalore 3-5yrs 15LPA") to the backend, which parses
 * it into { role_name, skills, location, experience_min, experience_max,
 * budget_min, budget_max, budget_min_strict, budget_max_strict }.
 *
 * Only search_text is sent — nothing else from formdata. Same auth
 * header and { status, response | error } return shape as parseCoffeeePlusJD.
 *
 * @param {{ search_text: string }} formdata
 */
function parseCoffeeePlusKeywords(formdata = {}) {
  let axiosRequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let currentUser = localStorage.getItem("jwtToken");
  if (currentUser) {
    axiosRequestOptions.headers["authorization"] = `Bearer ${currentUser}`;
  }

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}demand/coffeee-plus-parse-keywords`,
      { search_text: formdata.search_text },
      axiosRequestOptions
    )
    .then((response) => {
      return { status: true, response };
    })
    .catch((error) => {
      // Status only — never log the query or the full error response.
      console.error("Error in parseCoffeeePlusKeywords:", error.response?.status || error.message);
      return { status: false, error };
    });
}

/**
 * Review dashboard: attendance (Mon–Fri) + Sourced / Assessed / Sent and
 * the Sent breakdown, per recruiter per month.
 * @param {string} from "YYYY-MM"
 * @param {string} to   "YYYY-MM"
 */
function getReviewDashboard(from, to) {
  return get(`demand/review-dashboard?from=${from}&to=${to}`);
}